<template>
  <DashboardWidget
    title="Wiki"
    icon="fa fa-book"
    :view-all-link="{ name: 'WikiHome' }"
    :loading="loading"
    :is-empty="!hasPages"
    empty-icon="fa fa-book"
    empty-title="No wiki pages"
    empty-text="Create documentation for your team"
  >
    <div class="wiki-summary">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-value">{{ totalPages }}</span>
          <span class="stat-label">Pages</span>
        </div>
        <div class="stat-item">
          <span class="stat-value pinned">{{ pinnedCount }}</span>
          <span class="stat-label">Pinned</span>
        </div>
        <div class="stat-item">
          <span class="stat-value templates">{{ templatesCount }}</span>
          <span class="stat-label">Templates</span>
        </div>
      </div>

      <!-- Pinned Pages -->
      <div v-if="pinnedPages.length > 0" class="wiki-section">
        <div class="list-header">
          <i class="fa fa-thumb-tack"></i> Pinned
        </div>
        <div
          v-for="page in pinnedPages"
          :key="page.WikiPageID"
          class="list-item wiki-item"
          @click="goToPage(page)"
        >
          <div class="item-avatar wiki-avatar pinned">
            <i class="fa fa-thumb-tack"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ page.Name }}</span>
            </div>
            <p class="item-preview">
              {{ getTags(page) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Recent Pages -->
      <div v-if="recentPages.length > 0" class="wiki-section">
        <div class="list-header">Recently Updated</div>
        <div
          v-for="page in recentPages"
          :key="page.WikiPageID"
          class="list-item wiki-item"
          @click="goToPage(page)"
        >
          <div class="item-avatar wiki-avatar" :class="getPageTypeClass(page)">
            <i :class="getPageIcon(page)"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ page.Name }}</span>
              <span class="item-time">{{ getTimeAgo(page.UpdatedAt || page.CreatedAt) }}</span>
            </div>
            <p class="item-preview">
              {{ getTags(page) || 'No tags' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

export default {
  name: 'WikiWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxPinned: {
      type: Number,
      default: 2
    },
    maxRecent: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      isLoadingWiki: true
    };
  },

  computed: {
    ...mapState('wiki', ['wikipages']),

    loading() {
      return this.isLoadingWiki;
    },

    hasPages() {
      return this.wikipages && this.wikipages.length > 0;
    },

    totalPages() {
      return this.wikipages ? this.wikipages.length : 0;
    },

    pinnedCount() {
      if (!this.wikipages) return 0;
      return this.wikipages.filter(p => p.IsPinned === 1 || p.IsPinned === true).length;
    },

    templatesCount() {
      if (!this.wikipages) return 0;
      return this.wikipages.filter(p => p.IsEmailTemplate || p.IsSmsTemplate).length;
    },

    pinnedPages() {
      if (!this.wikipages) return [];
      return this.wikipages
        .filter(p => p.IsPinned === 1 || p.IsPinned === true)
        .slice(0, this.maxPinned);
    },

    recentPages() {
      if (!this.wikipages) return [];
      // Exclude pinned pages, sort by UpdatedAt descending
      return [...this.wikipages]
        .filter(p => !(p.IsPinned === 1 || p.IsPinned === true))
        .sort((a, b) => {
          const dateA = new Date(a.UpdatedAt || a.CreatedAt || 0);
          const dateB = new Date(b.UpdatedAt || b.CreatedAt || 0);
          return dateB - dateA;
        })
        .slice(0, this.maxRecent);
    }
  },

  async mounted() {
    await this.loadWikiPages();
  },

  methods: {
    ...mapActions('wiki', ['getWikiPages']),

    async loadWikiPages() {
      this.isLoadingWiki = true;
      try {
        await this.getWikiPages({ siteID: this.siteID });
      } catch (error) {
        console.error('Error loading wiki pages:', error);
      } finally {
        this.isLoadingWiki = false;
      }
    },

    goToPage(page) {
      this.$router.push({ name: 'WikiView', params: { wikiPageID: page.WikiPageID } });
    },

    getTimeAgo(date) {
      if (!date) return '';
      return moment(date).fromNow();
    },

    getTags(page) {
      if (!page.Tags) return '';
      return page.Tags;
    },

    getPageTypeClass(page) {
      if (page.IsEmailTemplate) return 'email-template';
      if (page.IsSmsTemplate) return 'sms-template';
      if (page.IsPublic) return 'public';
      return 'standard';
    },

    getPageIcon(page) {
      if (page.IsEmailTemplate) return 'fa fa-envelope';
      if (page.IsSmsTemplate) return 'fa fa-mobile';
      if (page.IsPublic) return 'fa fa-globe';
      return 'fa fa-file-text-o';
    }
  }
};
</script>

<style lang="scss" scoped>
.wiki-summary {
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    margin-bottom: 0;
    min-height: 60px;
  }

  .stat-item {
    display: block !important;
    text-align: center;
    flex: 1;
    min-width: 0;
    padding: 0 8px !important;
    border-left: none !important;
  }

  .stat-value {
    display: block;
    font-size: var(--widget-stat-size, 1.5rem);
    font-weight: 700;
    color: #334155;
    line-height: 1.2;

    &.pinned {
      color: #f59e0b;
    }

    &.templates {
      color: #8b5cf6;
    }
  }

  .stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .wiki-section {
    margin-bottom: 0;

    .list-header {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: var(--widget-list-padding, 8px 20px 4px 20px);
      display: flex;
      align-items: center;
      gap: 6px;

      i {
        font-size: 0.5625rem;
      }
    }
  }

  .wiki-item {
    cursor: pointer;
    transition: background-color 0.15s ease;
    padding: var(--widget-list-item-padding, 10px 20px);

    &:hover {
      background-color: #f8fafc;
    }
  }

  .wiki-avatar {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.pinned {
      background-color: rgba(245, 158, 11, 0.1);
      color: #f59e0b;
    }

    &.standard {
      background-color: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    &.email-template {
      background-color: rgba(139, 92, 246, 0.1);
      color: #8b5cf6;
    }

    &.sms-template {
      background-color: rgba(34, 197, 94, 0.1);
      color: #22c55e;
    }

    &.public {
      background-color: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    i {
      font-size: 0.875rem;
    }
  }
}
</style>
